<template>
  <div class="integrations-container">
    <ul class="cards-grid">
      <IntegrationCard
        v-for="integration in sortedIntegrations"
        :key="integration.name"
        :integration="integration"
        @toggle="toggleIntegration"
        @request-enable="handleRequestEnable"
      />
    </ul>
  </div>
</template>

<script setup lang="ts">
import { onMounted, watch } from 'vue';

import { useTrack, TrackEvents } from '@/composables/useTrack';
import { useToast } from '@/composables/useToast';
import { useIntegrationStore } from '@/modules/dashboard/integrations/store/useIntegrationStore';
import { useUserStore } from '@/store/useUserStore';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import { storeToRefs } from 'pinia';
import { paragon } from '@useparagon/connect';
import type { IntegrationInstallEvent, IntegrationUninstallEvent, WorkflowStateChangeEvent } from '@useparagon/connect';
import IntegrationCard from '../cards/IntegrationCard.vue';

const { track } = useTrack();
const { success, error } = useToast();

const integrationStore = useIntegrationStore();
const userStore = useUserStore();
const organizationStore = useOrganizationStore();
const { token, projectId, sortedIntegrations } = storeToRefs(integrationStore);

onMounted(() => integrationStore.fetchIntegrations());

const AUTH_FAILURE_MESSAGE = 'Failed to connect to integrations. Please refresh the page and try again.';

watch(token, newToken => {
  paragon.authenticate(projectId.value ?? '', newToken ?? '').catch(() => error(AUTH_FAILURE_MESSAGE));
});

const toggleIntegration = async (name: string) => {
  try {
    await paragon.authenticate(projectId.value ?? '', token.value ?? '');
  } catch {
    error(AUTH_FAILURE_MESSAGE);
    return;
  }
  const integrationName = name.toLowerCase();
  paragon.connect(integrationName, {
    onInstall: (event: IntegrationInstallEvent) => {
      track(TrackEvents.INTEGRATION_ENABLED, {
        integration_name: integrationName,
        integration_id: event.integrationId,
        integration_type: 'workflow',
        user_id: userStore.id,
        organization_id: organizationStore.id,
      });
      integrationStore.fetchIntegrations(true);
    },
    onUninstall: (event: IntegrationUninstallEvent) => {
      track(TrackEvents.INTEGRATION_DISABLED, {
        integration_name: integrationName,
        integration_id: event.integrationId,
        integration_type: 'workflow',
        user_id: userStore.id,
        organization_id: organizationStore.id,
      });
      integrationStore.fetchIntegrations(true);
    },
    onWorkflowChange: (event: WorkflowStateChangeEvent) => {
      const enabled = event.workflowStateChange?.enabled;
      let trackEvent: TrackEvents;
      if (enabled === true) {
        trackEvent = TrackEvents.WORKFLOW_ENABLED;
      } else if (enabled === false) {
        trackEvent = TrackEvents.WORKFLOW_DISABLED;
      } else {
        trackEvent = TrackEvents.WORKFLOW_PROPERTY_CHANGED;
      }
      track(trackEvent, {
        integration_name: integrationName,
        integration_id: event.integrationId,
        workflow_id: event.workflowId,
        user_id: userStore.id,
        organization_id: organizationStore.id,
      });
      integrationStore.fetchIntegrations(true);
    },
  });
};

const handleRequestEnable = (name: string) => {
  track(TrackEvents.INTEGRATIONS_SETUP, {
    app: name,
    coming_soon: false,
    setup_requested: true,
  });
  success(`Thanks for your interest in ${name}! We've recorded your request and will follow up with pricing and availability.`, {
    title: 'Integration Request Submitted',
  });
};
</script>

<style scoped>
.integrations-container {
  @apply py-8;
}
.cards-grid {
  @apply grid gap-6 mt-4;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
}
</style>
